import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Minus, ShoppingCart, Truck, ArrowLeft, Star, ZoomIn, X } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [lightbox, setLightbox] = useState(false);
  const { addItem } = useCart();
  const { format, currency } = useCurrency();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, text: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [userHasReviewed, setUserHasReviewed] = useState(false);

  const loadReviews = () =>
    api.get(`/products/${id}/reviews`).then(r => {
      setReviews(r.data);
      if (user) setUserHasReviewed(r.data.some(rv => rv.user_id === user.user_id));
    }).catch(() => {});

  useEffect(() => {
    api.get(`/products/${id}`).then(r => {
      setP(r.data);
      document.title = `${r.data.name} – Cheap4Teach`;
    }).catch(() => nav("/shop"));
    loadReviews();
  }, [id, nav]);

  if (!p) return <div className="max-w-7xl mx-auto p-10">Loading...</div>;

  const soldOut = (p.stock ?? 0) <= 0;
  const lowStock = !soldOut && (p.stock ?? 0) <= 3;
  const handleAdd = () => {
    if (soldOut) return;
    addItem(p, qty);
    toast.success(`Added ${qty} × ${p.name} to cart!`);
  };

  return (
    <>
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 text-[#118AB2] font-semibold mb-6 hover:text-[#FF477E]">
        <ArrowLeft className="w-4 h-4" /> Back to Shop
      </Link>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="c4t-card !p-4">
          <div className="relative aspect-square overflow-hidden rounded-2xl bg-[#FFF9F2] border-2 border-[#073B4C]/20 group cursor-zoom-in" onClick={() => p.image && setLightbox(true)}>
            <img src={p.image} alt={p.name} className={`w-full h-full object-cover ${soldOut ? "opacity-60" : ""}`} data-testid="product-image" />
            {p.image && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-all duration-200 pointer-events-none">
                <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 drop-shadow-lg transition-opacity duration-200" />
              </div>
            )}
            {soldOut && (
              <span className="absolute top-4 left-4 px-4 py-1.5 bg-black text-white text-sm font-['Fredoka'] font-bold rounded-full border-2 border-white shadow-[3px_3px_0_#000]"
                data-testid="product-sold-out-badge">SOLD OUT</span>
            )}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="c4t-badge">{p.category}</span>
            {p.sale_price > 0 && (
              <span className="px-3 py-1 bg-[#FF1F87] text-white text-xs font-['Fredoka'] font-bold rounded-full">SALE</span>
            )}
          </div>
          <h1 className="text-4xl sm:text-5xl font-['Fredoka'] font-bold mt-3" data-testid="product-name">{p.name}</h1>
          {reviews.length > 0 ? (
            <div className="flex items-center gap-1 mt-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`w-5 h-5 ${i < Math.round(reviews.reduce((s,r)=>s+r.rating,0)/reviews.length) ? "fill-[#FFD166] text-[#FFD166]" : "text-gray-300"}`} />
              ))}
              <span className="text-[#455A64] ml-2 font-semibold text-sm">
                {(reviews.reduce((s,r)=>s+r.rating,0)/reviews.length).toFixed(1)} ({reviews.length} review{reviews.length !== 1 ? "s" : ""})
              </span>
            </div>
          ) : (
            <p className="text-sm text-[#455A64] mt-2">No reviews yet. Be the first!</p>
          )}
          {p.sale_price > 0 ? (
            <div className="flex items-baseline gap-3 mt-4">
              <p className="text-4xl text-[#FF1F87] font-bold" data-testid="product-price">{format(p.sale_price, p.currency)} {currency}</p>
              <p className="text-xl text-gray-400 line-through">{format(p.price, p.currency)}</p>
            </div>
          ) : (
            <p className="text-4xl text-[#FF1F87] font-bold mt-4" data-testid="product-price">{format(p.price, p.currency)} {currency}</p>
          )}
          <p className="text-[#455A64] text-lg mt-4 leading-relaxed" data-testid="product-description">{p.description}</p>

          <div className="mt-6 flex items-center gap-4">
            <span className="font-bold">Quantity:</span>
            <div className="flex items-center border-[3px] border-[#073B4C] rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} disabled={soldOut} className="p-3 disabled:opacity-40" data-testid="detail-qty-dec"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-bold text-lg" data-testid="detail-qty-val">{qty}</span>
              <button onClick={() => setQty(Math.min(p.stock || 1, qty + 1))} disabled={soldOut} className="p-3 disabled:opacity-40" data-testid="detail-qty-inc"><Plus className="w-4 h-4" /></button>
            </div>
            <span className="text-sm text-[#455A64]" data-testid="product-stock">
              {soldOut ? "Out of stock" : `In stock: ${p.stock}`}
            </span>
          </div>

          {lowStock && (
            <div className="mt-3 inline-flex items-center gap-2 bg-[#FFF3E6] border-2 border-[#FF6B1A] rounded-full px-4 py-1.5 animate-pulse" data-testid="product-low-stock">
              <span className="text-[#FF6B1A] font-bold text-sm">Only {p.stock} left! Grab it before it's gone.</span>
            </div>
          )}

          <button onClick={handleAdd} disabled={soldOut}
            className={`mt-6 c4t-btn w-full sm:w-auto ${soldOut ? "c4t-btn-white !opacity-60 cursor-not-allowed" : "c4t-btn-pink"}`}
            data-testid="detail-add-to-cart">
            <ShoppingCart className="w-5 h-5" /> {soldOut ? "Sold out" : "Add to Cart"}
          </button>

          <div className="mt-6 c4t-card !p-5 !shadow-[4px_4px_0_#073B4C] bg-[#FFF9F2]">
            <p className="flex items-start gap-3 text-[#073B4C]">
              <Truck className="w-6 h-6 mt-1 text-[#118AB2]" strokeWidth={2.5} />
              <span><strong>Shipping note:</strong> We ship worldwide. Shipping cost varies by country. You'll get a personalized quote based on your address at checkout.</span>
            </p>
          </div>
        </div>
      </div>
    </div>

      {/* ── Reviews Section ── */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        <h2 className="text-3xl font-['Fredoka'] font-bold mb-6">Customer Reviews</h2>

        {/* Existing reviews */}
        {reviews.length === 0 ? (
          <p className="c4t-desc mb-6">No reviews yet.</p>
        ) : (
          <div className="space-y-4 mb-10">
            {reviews.map(rv => (
              <div key={rv.id} className="c4t-card">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <p className="font-bold">{rv.reviewer_name}</p>
                    <div className="flex gap-0.5 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < rv.rating ? "fill-[#FFD166] text-[#FFD166]" : "text-gray-300"}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs c4t-desc">{new Date(rv.created_at).toLocaleDateString("en-US", {year:"numeric",month:"short",day:"numeric"})}</p>
                </div>
                <p className="mt-2 text-sm">{rv.text}</p>
              </div>
            ))}
          </div>
        )}

        {/* Review form — verified purchasers only */}
        {!user ? (
          <p className="c4t-desc text-sm">
            <Link to="/login" className="text-[#FF1F87] font-semibold hover:underline">Sign in</Link> to leave a review (verified purchasers only).
          </p>
        ) : userHasReviewed ? (
          <p className="c4t-desc text-sm">You have already reviewed this product. Thank you!</p>
        ) : (
          <div className="c4t-card max-w-xl">
            <h3 className="font-['Fredoka'] font-bold text-xl mb-4">Leave a Review</h3>
            <div className="flex gap-1 mb-3">
              {[1,2,3,4,5].map(star => (
                <button key={star} type="button" onClick={() => setReviewForm(f => ({ ...f, rating: star }))}
                  className="p-1 focus:outline-none">
                  <Star className={`w-7 h-7 transition-colors ${star <= reviewForm.rating ? "fill-[#FFD166] text-[#FFD166]" : "text-gray-300 hover:text-[#FFD166]"}`} />
                </button>
              ))}
            </div>
            <textarea
              className="c4t-input w-full h-28 resize-none"
              placeholder="Share your experience with this product..."
              value={reviewForm.text}
              onChange={e => setReviewForm(f => ({ ...f, text: e.target.value }))}
            />
            <button
              disabled={submittingReview || !reviewForm.text.trim()}
              onClick={async () => {
                setSubmittingReview(true);
                try {
                  await api.post(`/products/${id}/reviews`, reviewForm);
                  toast.success("Review submitted! Thank you.");
                  setReviewForm({ rating: 5, text: "" });
                  loadReviews();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Could not submit review. Only verified purchasers can leave reviews.");
                } finally { setSubmittingReview(false); }
              }}
              className="c4t-btn c4t-btn-pink mt-3 w-full disabled:opacity-50">
              {submittingReview ? "Submitting…" : "Submit Review"}
            </button>
          </div>
        )}
      </div>

      {/* ── Image Lightbox ── */}
      {lightbox && p.image && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(false)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#FF1F87] transition-colors"
            onClick={() => setLightbox(false)}
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={p.image}
            alt={p.name}
            className="max-w-full max-h-[90vh] rounded-2xl shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-0 right-0 text-center text-white/70 text-sm font-['Fredoka']">{p.name}</p>
        </div>
      )}
    </>
  );
}
